/**
 * Shared building blocks for the Forms pages — built from the component library and
 * following the TaskManagement patterns (clickable stat cards, titled panels with a
 * divider, colour-rail distribution bars, avatar + name cells).
 */

import type { CSSProperties, ReactNode } from 'react';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { Card, Group, Stack, Text, Title, ThemeIcon, Tooltip, Box, Skeleton, SimpleGrid, Button, ActionIcon } from '@/components/ui';
import { Avatar, Divider } from '@/components/ui/data-display';
import { CopyButton } from '@/components';
import { formatStampDateTime } from './formsUtils';
import { colorFor, initials, relativeTime } from './formsDisplay';

// ---------------------------------------------------------------- stat tile

interface StatTileProps {
  icon: ReactNode;
  color: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  /** Highlighted when this tile is the active quick filter. */
  active?: boolean;
  /** Red/orange left rail for things that need attention. */
  emphasis?: boolean;
  onClick?: () => void;
  /** data-help-id for in-app help tours. */
  helpId?: string;
}

/** Clickable stat card — the TaskManagement dashboard / assigned-tasks pattern. */
export function FormsStatTile({ icon, color, label, value, hint, active, emphasis, onClick, helpId }: StatTileProps) {
  return (
    <Card
      data-help-id={helpId}
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        transition: 'background-color 150ms ease',
        backgroundColor: active ? `var(--mantine-color-${color}-light)` : undefined,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Rails are strips, not borders — the theme forces card border colours. */}
      {(active || emphasis) && (
        <Box
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: `var(--mantine-color-${color}-6)`,
          }}
        />
      )}
      <Group wrap="nowrap" gap="md" align="center">
        <ThemeIcon size={44} radius="md" variant={active ? 'filled' : 'light'} color={color} style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
        <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lineClamp={1}>
            {label}
          </Text>
          <Text size="xl" fw={700} lh={1.2}>
            {value}
          </Text>
          {hint && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {hint}
            </Text>
          )}
        </Stack>
      </Group>
    </Card>
  );
}

export function StatTilesSkeleton({ count = 4 }: { count?: number }) {
  return (
    <SimpleGrid cols={{ base: 2, md: count }}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} height={86} radius="md" />
      ))}
    </SimpleGrid>
  );
}

// ---------------------------------------------------------------- section card

interface SectionCardProps {
  icon?: ReactNode;
  color?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** No divider under the header (e.g. when the body is a table). */
  noDivider?: boolean;
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  h?: string | number;
  style?: CSSProperties;
  /** data-help-id for in-app help tours. */
  helpId?: string;
}

/** Titled panel — icon + title + description on the left, actions on the right. */
export function SectionCard({ icon, color = 'blue', title, description, actions, children, noDivider, padding = 'lg', h, style, helpId }: SectionCardProps) {
  return (
    <Card shadow="sm" padding={padding} radius="md" withBorder h={h} style={style} data-help-id={helpId}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb={noDivider ? 'sm' : 'xs'}>
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          {icon && (
            <ThemeIcon size="lg" radius="md" variant="light" color={color} style={{ flexShrink: 0 }}>
              {icon}
            </ThemeIcon>
          )}
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Title order={4} lineClamp={1}>
              {title}
            </Title>
            {description && (
              <Text size="xs" c="dimmed">
                {description}
              </Text>
            )}
          </Stack>
        </Group>
        {actions && (
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {actions}
          </Group>
        )}
      </Group>
      {!noDivider && <Divider mb="md" />}
      {children}
    </Card>
  );
}

// ---------------------------------------------------------------- meter bar

interface MeterSegment {
  value: number;
  color: string;
  label?: string;
}

interface MeterBarProps {
  /** 0–100, or use `segments` for a stacked bar. */
  value?: number;
  color?: string;
  segments?: MeterSegment[];
  height?: number;
  tooltip?: ReactNode;
}

/** Plain colour bar (the TaskManagement distribution-bar pattern). */
export function MeterBar({ value = 0, color = 'blue', segments, height = 8, tooltip }: MeterBarProps) {
  const parts: MeterSegment[] = segments ?? [{ value, color }];
  const bar = (
    <Box
      style={{
        width: '100%',
        height,
        borderRadius: height / 2,
        backgroundColor: 'var(--mantine-color-default-border)',
        overflow: 'hidden',
        display: 'flex',
      }}
    >
      {parts.map((p, i) => (
        <Box
          key={i}
          style={{
            width: `${Math.max(0, Math.min(100, p.value))}%`,
            height: '100%',
            backgroundColor: `var(--mantine-color-${p.color}-6)`,
            transition: 'width 250ms ease',
          }}
        />
      ))}
    </Box>
  );
  return tooltip ? <Tooltip label={tooltip}>{bar}</Tooltip> : bar;
}

interface MeterRowProps extends MeterBarProps {
  label: ReactNode;
  valueLabel: ReactNode;
  icon?: ReactNode;
}

export function MeterRow({ label, valueLabel, icon, ...bar }: MeterRowProps) {
  return (
    <Stack gap={4}>
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          {icon}
          <Text size="sm" lineClamp={1}>
            {label}
          </Text>
        </Group>
        <Text size="sm" fw={600} style={{ flexShrink: 0 }}>
          {valueLabel}
        </Text>
      </Group>
      <MeterBar {...bar} />
    </Stack>
  );
}

// ---------------------------------------------------------------- people

interface PersonCellProps {
  name: string;
  secondary?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  avatarKey?: string | null;
}

export function PersonAvatar({ name, size = 'md', avatarKey }: { name: string; size?: 'sm' | 'md' | 'lg' | 'xl'; avatarKey?: string | null }) {
  return (
    <Avatar size={size} radius="xl" color={colorFor(avatarKey || name)} variant="light">
      {initials(name)}
    </Avatar>
  );
}

/** Avatar + name + secondary line (email / form / ref). */
export function PersonCell({ name, secondary, size = 'md', avatarKey }: PersonCellProps) {
  return (
    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
      <PersonAvatar name={name} size={size} avatarKey={avatarKey} />
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="sm" fw={600} lineClamp={1}>
          {name}
        </Text>
        {secondary && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {secondary}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

// ---------------------------------------------------------------- time

/** "3 days ago" with the exact date/time on hover. */
export function RelativeTime({ stamp, size = 'sm', c, prefix }: { stamp: number | null | undefined; size?: 'xs' | 'sm' | 'md'; c?: string; prefix?: string }) {
  if (!stamp) return <Text size={size} c="dimmed">—</Text>;
  return (
    <Tooltip label={formatStampDateTime(stamp)} withArrow>
      <Text size={size} c={c} span style={{ whiteSpace: 'nowrap' }}>
        {prefix ? `${prefix} ` : ''}
        {relativeTime(stamp)}
      </Text>
    </Tooltip>
  );
}

// ---------------------------------------------------------------- reference chip

/** Monospace reference that never wraps; click copies it. */
export function RefChip({ value, size = 'xs' }: { value: string; size?: 'xs' | 'sm' }) {
  return (
    <CopyButton value={value}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? 'Copied' : 'Copy reference'} withArrow>
          <button
            type="button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              copy();
            }}
            style={{
              display: 'inline-flex',
              alignSelf: 'flex-start',
              width: 'fit-content',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
              fontFamily: 'var(--mantine-font-family-monospace)',
              fontSize: size === 'xs' ? 12 : 13,
              padding: '1px 6px',
              borderRadius: 4,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'var(--mantine-color-default)',
              color: 'var(--mantine-color-text)',
              cursor: 'pointer',
            }}
          >
            {value}
            {copied ? <IconCheck size={11} /> : <IconCopy size={11} style={{ opacity: 0.55 }} />}
          </button>
        </Tooltip>
      )}
    </CopyButton>
  );
}

// ---------------------------------------------------------------- detail row

/** Icon + small label + value (the TaskInfoCard DetailRow pattern). */
export function DetailRow({ icon, label, children }: { icon: ReactNode; label: ReactNode; children: ReactNode }) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <Box style={{ color: 'var(--mantine-color-dimmed)', flexShrink: 0, paddingTop: 2 }}>{icon}</Box>
      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        {children}
      </Stack>
    </Group>
  );
}

// ---------------------------------------------------------------- copy action

interface CopyActionProps {
  value: string;
  label?: string;
  copiedLabel?: string;
  size?: 'xs' | 'sm' | 'md';
  variant?: 'filled' | 'light' | 'default' | 'subtle' | 'outline';
  color?: string;
  /** Small icon button (with a tooltip) instead of a labelled button. */
  iconOnly?: boolean;
}

/** Labelled copy button — the library's icon-only one is hard to see on dark backgrounds. */
export function CopyAction({ value, label = 'Copy', copiedLabel = 'Copied', size = 'sm', variant = 'light', color = 'blue', iconOnly }: CopyActionProps) {
  return (
    <CopyButton value={value}>
      {({ copied, copy }) =>
        iconOnly ? (
          <Tooltip label={copied ? copiedLabel : label} withArrow>
            <ActionIcon variant={variant} color={copied ? 'teal' : color} onClick={copy} aria-label={label} size={size === 'xs' ? 'sm' : 'md'}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        ) : (
          <Button size={size} variant={variant} color={copied ? 'teal' : color} leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />} onClick={copy}>
            {copied ? copiedLabel : label}
          </Button>
        )
      }
    </CopyButton>
  );
}

// ---------------------------------------------------------------- code + empty blocks

/** Monospace block for snippets (wraps long lines, subtle panel on both themes). */
export function CodeBlock({ children, maxHeight }: { children: ReactNode; maxHeight?: number }) {
  return (
    <pre
      style={{
        margin: 0,
        padding: 'var(--mantine-spacing-sm)',
        fontFamily: 'var(--mantine-font-family-monospace)',
        fontSize: 12,
        lineHeight: 1.55,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        borderRadius: 'var(--mantine-radius-md)',
        border: '1px solid var(--mantine-color-default-border)',
        background: 'var(--mantine-color-default)',
        color: 'var(--mantine-color-text)',
        maxHeight,
        overflow: maxHeight ? 'auto' : undefined,
      }}
    >
      {children}
    </pre>
  );
}

/** Small "nothing here yet" block for inside a card (EmptyState is for whole pages / tables). */
export function InlineEmpty({ icon, title, description, action }: { icon: ReactNode; title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <Stack align="center" gap={6} py="md">
      <ThemeIcon size={44} radius="xl" variant="light" color="gray">
        {icon}
      </ThemeIcon>
      <Text fw={600} size="sm">
        {title}
      </Text>
      {description && (
        <Text size="xs" c="dimmed" ta="center" maw={340}>
          {description}
        </Text>
      )}
      {action}
    </Stack>
  );
}
